import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import * as validators from '../../../util/validators';
import { isSignupEmailTakenError } from '../../../util/errors';
import { getPropsForCustomUserFieldInputs } from '../../../util/userHelpers';
import { ReactComponent as Impressum } from '../../../assets/icons/impressum.svg';

import {
  Form,
  PrimaryButton,
  Button,
  FieldTextInput,
  CustomExtendedDataField,
  SecondaryButton,
  H3,
} from '../../../components';

import FieldSelectUserType from '../FieldSelectUserType';
import UserFieldDisplayName from '../UserFieldDisplayName';
import UserFieldPhoneNumber from '../UserFieldPhoneNumber';
import MarketingConsent from '../MarketingConsent/MarketingConsent';

import css from './SignupForm.module.css';
import eyeIcon from '../../../assets/icons/eye.svg';
import eyeSlashIcon from '../../../assets/icons/Union.svg';

const getSoleUserTypeMaybe = userTypes => {
  return Array.isArray(userTypes) && userTypes.length === 1 ? userTypes[0].userType : null;
};

const SignupFormComponent = props => {
  const [showPassword, setShowPassword] = React.useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = React.useState(false);

  return (
    <FinalForm
      {...props}
      mutators={{ ...arrayMutators }}
      initialValues={{
        // userType is always non business user at the start
        // if this changes at some point replace the line below with:
        // userType: props.preselectedUserType || getSoleUserTypeMaybe(props.userTypes)
        // Also uncomment FieldSelectUserType component down below
        userType: 'non-business-user',
      }}
      onSubmit={values => {
        const {
          fullName,
          email,
          password,
          username,
          userType,
          phoneNumber,
          pub_impressum_address,
          pub_impressum_dispute_link,
          pub_impressum_email,
          pub_impressum_name,
          pub_impressum_phone_number,
          pub_impressum_registration_number,
          pub_impressum_responsible_person,
          pub_impressum_vat_id,
          pub_username,
          marketingConsent,
          terms
        } = values;

        const [firstName, ...rest] = fullName.trim().split(' ');
        const lastName = rest.join(' ') || '-';

        return props.onSubmit({
          firstName,
          lastName,
          email,
          password,
          marketingConsent: !!marketingConsent,
          publicData: {
            fullname: fullName,
            username: pub_username,
            userType,
            impressum_address: pub_impressum_address,
            impressum_dispute_link: pub_impressum_dispute_link,
            impressum_email: pub_impressum_email,
            impressum_name: pub_impressum_name,
            impressum_phone_number: pub_impressum_phone_number,
            impressum_registration_number: pub_impressum_registration_number,
            impressum_responsible_person: pub_impressum_responsible_person,
            impressum_vat_id: pub_impressum_vat_id,

          },
          protectedData: {
            marketingConsent: !!marketingConsent,
            ...(phoneNumber ? { phoneNumber } : {}),
            ...(Array.isArray(terms) && terms.includes('tos-and-privacy')
              ? { termsAccepted: true }
              : {}),
          },
        });
      }}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          formId,
          handleSubmit,
          inProgress,
          invalid,
          intl,
          termsAndConditions,
          preselectedUserType,
          userTypes,
          userFields,
          values,
          onClose,
        } = formRenderProps;

        const { userType } = values || {};

        // email
        const emailRequired = validators.required(
          intl.formatMessage({
            id: 'SignupForm.emailRequired',
          })
        );
        const emailValid = validators.emailFormatValid(
          intl.formatMessage({
            id: 'SignupForm.emailInvalid',
          })
        );
        // If API responded with email already taken (409), show field-level error under email
        const emailTakenCustomError =
          props.signupError && isSignupEmailTakenError(props.signupError)
            ? intl.formatMessage({ id: 'ContactDetailsForm.emailTakenError' })
            : null;

        // password
        const passwordRequiredMessage = intl.formatMessage({
          id: 'SignupForm.passwordRequired',
        });
        const passwordMinLengthMessage = intl.formatMessage(
          {
            id: 'SignupForm.passwordTooShort',
          },
          {
            minLength: validators.PASSWORD_MIN_LENGTH,
          }
        );
        const passwordMaxLengthMessage = intl.formatMessage(
          {
            id: 'SignupForm.passwordTooLong',
          },
          {
            maxLength: validators.PASSWORD_MAX_LENGTH,
          }
        );
        const passwordMinLength = validators.minLength(
          passwordMinLengthMessage,
          validators.PASSWORD_MIN_LENGTH
        );
        const passwordMaxLength = validators.maxLength(
          passwordMaxLengthMessage,
          validators.PASSWORD_MAX_LENGTH
        );
        const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);
        const passwordHasLetterAndNumber = value => {
          const val = value || '';
          const hasLetter = /[A-Za-z]/.test(val);
          const hasNumber = /\d/.test(val);
          return hasLetter && hasNumber
            ? undefined
            : intl.formatMessage({ id: 'SignupForm.passwordHelper' });
        };

        const passwordValidators = validators.composeValidators(
          passwordRequired,
          passwordMinLength,
          passwordMaxLength,
          passwordHasLetterAndNumber
        );

        // password confirmation
        const passwordConfirmRequiredMessage = intl.formatMessage({
          id: 'SignupForm.passwordConfirmRequired',
        });
        const passwordDoNotMatchMessage = intl.formatMessage({
          id: 'SignupForm.passwordsDoNotMatch',
        });
        const passwordConfirmRequired = validators.requiredStringNoTrim(
          passwordConfirmRequiredMessage
        );
        const passwordConfirmMatches = (value, allValues) => {
          const original = allValues?.password || '';
          return value === original ? undefined : passwordDoNotMatchMessage;
        };
        const passwordConfirmValidators = validators.composeValidators(
          passwordConfirmRequired,
          passwordConfirmMatches
        );

        // Custom user fields. Since user types are not supported here,
        // only fields with no user type id limitation are selected.
        const userFieldProps = getPropsForCustomUserFieldInputs(userFields, intl, userType);

        const noUserTypes = !userType && !(userTypes?.length > 0);
        const userTypeConfig = userTypes.find(config => config.userType === userType);
        const showDefaultUserFields = userType || noUserTypes;
        const showCustomUserFields = (userType || noUserTypes) && userFieldProps?.length > 0;

        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = inProgress;
        const submitDisabled = invalid || submitInProgress;

        const usernameField = userFieldProps.find(item => item.fieldConfig.key === 'username');
        const impressumNameField = userFieldProps.find(
          item => item.fieldConfig.key === 'impressum_name'
        );
        const impressumAddressField = userFieldProps.find(
          item => item.fieldConfig.key === 'impressum_address'
        );
        const impressumEmailField = userFieldProps.find(
          item => item.fieldConfig.key === 'impressum_email'
        );
        const impressumPhoneField = userFieldProps.find(
          item => item.fieldConfig.key === 'impressum_phone_number'
        );
        const impressumVatIdField = userFieldProps.find(
          item => item.fieldConfig.key === 'impressum_vat_id'
        );
        const impressumRegistrationNumberField = userFieldProps.find(
          item => item.fieldConfig.key === 'impressum_registration_number'
        );
        const impressumResponsiblePersonField = userFieldProps.find(
          item => item.fieldConfig.key === 'impressum_responsible_person'
        );
        const impressumDisputeLinkField = userFieldProps.find(
          item => item.fieldConfig.key === 'impressum_dispute_link'
        );

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            {/*<FieldSelectUserType
              name="userType"
              userTypes={userTypes}
              hasExistingUserType={!!preselectedUserType}
              intl={intl}
            />*/}

            {showDefaultUserFields ? (
              <div className={css.defaultUserFields}>
                <FieldTextInput
                  className={css.firstNameRoot}
                  type="text"
                  id={formId ? `${formId}.fullName` : 'fullName'}
                  name="fullName"
                  autoComplete="name"
                  placeholder={`${intl.formatMessage({
                    id: 'SignupForm.fullNamePlaceholder',
                  })} *`}
                  helperText={intl.formatMessage({
                    id: 'SignupForm.fullNameHelper',
                  })}
                  validate={validators.composeValidators(
                    validators.required(intl.formatMessage({ id: 'SignupForm.fullNameRequired' })),
                    validators.minLength(
                      intl.formatMessage({ id: 'SignupForm.minChars' }, { min: 3 }),
                      3
                    )
                  )}
                />

                <CustomExtendedDataField
                  key={usernameField.key}
                  fieldConfig={usernameField.fieldConfig}
                  name={usernameField.name}
                  formId={formId}
                  defaultRequiredMessage={usernameField.defaultRequiredMessage}
                />

                {/*<FieldTextInput
                  className={css.lastNameRoot}
                  type="text"
                  id={formId ? `${formId}.username` : 'username'}
                  name="username"
                  autoComplete="username"
                  placeholder={`${intl.formatMessage({
                    id: 'SignupForm.usernamePlaceholder',
                  })} *`}
                  helperText={intl.formatMessage({
                    id: 'SignupForm.usernameHelper',
                  })}
                  validate={validators.composeValidators(
                    validators.required(intl.formatMessage({ id: 'SignupForm.usernameRequired' })),
                    validators.minLength(
                      intl.formatMessage({ id: 'SignupForm.usernameMinChars' }, { min: 3 }),
                      3
                    )
                  )}
                />*/}

                <FieldTextInput
                  type="email"
                  id={formId ? `${formId}.email` : 'email'}
                  name="email"
                  autoComplete="email"
                  placeholder={`${intl.formatMessage({
                    id: 'SignupForm.emailPlaceholder',
                  })} *`}
                  validate={validators.composeValidators(emailRequired, emailValid)}
                  customErrorText={emailTakenCustomError}
                />

                <UserFieldDisplayName
                  formName="SignupForm"
                  className={css.row}
                  userTypeConfig={userTypeConfig}
                  intl={intl}
                />

                <div className={classNames(css.password, css.passwordField)}>
                  <FieldTextInput
                    className={css.password}
                    inputRootClass={css.passwordInput}
                    type={showPassword ? 'text' : 'password'}
                    id={formId ? `${formId}.password` : 'password'}
                    name="password"
                    autoComplete="new-password"
                    placeholder={`${intl.formatMessage({
                      id: 'SignupForm.passwordPlaceholder',
                    })} *`}
                    helperText={intl.formatMessage({
                      id: 'SignupForm.passwordHelper',
                    })}
                    validate={passwordValidators}
                    validateFields={['passwordConfirm']}
                  />
                  <button
                    type="button"
                    className={css.passwordToggle}
                    onClick={() => setShowPassword(s => !s)}
                    aria-label={
                      showPassword
                        ? intl.formatMessage({
                            id: 'SignupForm.hidePassword',
                            defaultMessage: 'Hide password',
                          })
                        : intl.formatMessage({
                            id: 'SignupForm.showPassword',
                            defaultMessage: 'Show password',
                          })
                    }
                    aria-pressed={showPassword}
                  >
                    <img src={showPassword ? eyeIcon : eyeSlashIcon} alt="" />
                  </button>
                </div>

                <div className={classNames(css.password, css.passwordField)}>
                  <FieldTextInput
                    className={css.password}
                    inputRootClass={css.passwordInput}
                    type={showPasswordConfirm ? 'text' : 'password'}
                    id={formId ? `${formId}.passwordConfirm` : 'passwordConfirm'}
                    name="passwordConfirm"
                    autoComplete="new-password"
                    placeholder={`${intl.formatMessage({
                      id: 'SignupForm.passwordConfirmPlaceholder',
                    })} *`}
                    helperText={intl.formatMessage({
                      id: 'SignupForm.passwordConfirmHelper',
                    })}
                    validate={passwordConfirmValidators}
                  />
                  <button
                    type="button"
                    className={css.passwordToggle}
                    onClick={() => setShowPasswordConfirm(s => !s)}
                    aria-label={
                      showPasswordConfirm
                        ? intl.formatMessage({
                            id: 'SignupForm.hidePassword',
                            defaultMessage: 'Hide password',
                          })
                        : intl.formatMessage({
                            id: 'SignupForm.showPassword',
                            defaultMessage: 'Show password',
                          })
                    }
                    aria-pressed={showPasswordConfirm}
                  >
                    <img src={showPasswordConfirm ? eyeIcon : eyeSlashIcon} alt="" />
                  </button>
                </div>

                <UserFieldPhoneNumber
                  formName="SignupForm"
                  className={css.row}
                  userTypeConfig={userTypeConfig}
                  intl={intl}
                />
              </div>
            ) : null}

            {/*{showCustomUserFields ? (
              <div className={css.customFields}>
                {userFieldProps.map(({ key, ...fieldProps }) => (
                  <CustomExtendedDataField key={key} {...fieldProps} formId={formId} />
                ))}
              </div>
            ) : null}*/}

            {values.userType === 'business-user' && (
              <div className={css.impressum}>
                <div className={css.impressumHeader}>
                  <H3 as="h1" className={css.heading}>
                    <Impressum /> <FormattedMessage id="ProfileSettingsForm.Impressum" />
                  </H3>
                  {/*<p className={css.impressumDescription}>
                    <FormattedMessage id="ProfileSettingsForm.Impressum.Description" />
                  </p>*/}
                </div>
                <div className={css.horizontalFieldsWrap}>
                  <div className={css.horizontalFields}>
                    <CustomExtendedDataField
                      key={impressumNameField.key}
                      fieldConfig={impressumNameField.fieldConfig}
                      name={impressumNameField.name}
                      formId={formId}
                      defaultRequiredMessage={impressumNameField.defaultRequiredMessage}
                    />

                    <CustomExtendedDataField
                      key={impressumAddressField.key}
                      fieldConfig={impressumAddressField.fieldConfig}
                      name={impressumAddressField.name}
                      formId={formId}
                      defaultRequiredMessage={impressumAddressField.defaultRequiredMessage}
                    />
                  </div>

                  <div className={css.horizontalFields}>
                    <CustomExtendedDataField
                      key={impressumEmailField.key}
                      fieldConfig={impressumEmailField.fieldConfig}
                      name={impressumEmailField.name}
                      formId={formId}
                      defaultRequiredMessage={impressumEmailField.defaultRequiredMessage}
                    />

                    <CustomExtendedDataField
                      key={impressumPhoneField.key}
                      fieldConfig={impressumPhoneField.fieldConfig}
                      name={impressumPhoneField.name}
                      formId={formId}
                      defaultRequiredMessage={impressumPhoneField.defaultRequiredMessage}
                    />
                  </div>

                  <div className={css.horizontalFields}>
                    <CustomExtendedDataField
                      key={impressumVatIdField.key}
                      fieldConfig={impressumVatIdField.fieldConfig}
                      name={impressumVatIdField.name}
                      formId={formId}
                      defaultRequiredMessage={impressumVatIdField.defaultRequiredMessage}
                    />

                    <CustomExtendedDataField
                      key={impressumRegistrationNumberField.key}
                      fieldConfig={impressumRegistrationNumberField.fieldConfig}
                      name={impressumRegistrationNumberField.name}
                      formId={formId}
                      defaultRequiredMessage={
                        impressumRegistrationNumberField.defaultRequiredMessage
                      }
                    />
                  </div>

                  <div className={css.horizontalFields}>
                    <CustomExtendedDataField
                      key={impressumResponsiblePersonField.key}
                      fieldConfig={impressumResponsiblePersonField.fieldConfig}
                      name={impressumResponsiblePersonField.name}
                      formId={formId}
                      defaultRequiredMessage={
                        impressumResponsiblePersonField.defaultRequiredMessage
                      }
                    />

                    <CustomExtendedDataField
                      key={impressumDisputeLinkField.key}
                      fieldConfig={impressumDisputeLinkField.fieldConfig}
                      name={impressumDisputeLinkField.name}
                      formId={formId}
                      defaultRequiredMessage={impressumDisputeLinkField.defaultRequiredMessage}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className={css.bottomWrapper}>
              {termsAndConditions}
              <MarketingConsent />
              <div className={css.modalButtons}>
                {onClose ? (
                  <SecondaryButton type="button" onClick={onClose} className={css.cancelButton}>
                    Abbrechen
                  </SecondaryButton>
                ) : null}
                <PrimaryButton
                  type="submit"
                  inProgress={submitInProgress}
                  disabled={submitDisabled}
                  className={css.submitButton}
                >
                  <FormattedMessage id="SignupForm.signUp" />
                  <svg
                    className={css.submitIcon}
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path
                      d="M19.4999 3.75C19.9141 3.75003 20.2499 4.08581 20.2499 4.5V15.75C20.2499 16.1642 19.9141 16.5 19.4999 16.5C19.0857 16.5 18.7499 16.1642 18.7499 15.75V6.31055L5.03022 20.0303C4.73733 20.3232 4.26257 20.3231 3.96967 20.0303C3.67678 19.7374 3.67678 19.2626 3.96967 18.9697L17.6894 5.25H8.24994C7.83573 5.25 7.49994 4.91421 7.49994 4.5C7.49994 4.08579 7.83573 3.75 8.24994 3.75H19.4999Z"
                      fill="currentColor"
                    />
                  </svg>
                </PrimaryButton>
              </div>
            </div>
          </Form>
        );
      }}
    />
  );
};

/**
 * A component that renders the signup form.
 *
 * @component
 * @param {Object} props
 * @param {string} props.rootClassName - The root class name that overrides the default class css.root
 * @param {string} props.className - The class that extends the root class
 * @param {string} props.formId - The form id
 * @param {boolean} props.inProgress - Whether the form is in progress
 * @param {ReactNode} props.termsAndConditions - The terms and conditions
 * @param {string} props.preselectedUserType - The preselected user type
 * @param {propTypes.userTypes} props.userTypes - The user types
 * @param {propTypes.listingFields} props.userFields - The user fields
 * @returns {JSX.Element}
 */
const SignupForm = props => {
  const intl = useIntl();
  return <SignupFormComponent {...props} intl={intl} />;
};

export default SignupForm;
